/**
 * İÇERİK KARNESİ — yazılan içeriklerin 30/60/90. gün hedef kelime performansı.
 * Veri gece otomatik ölçülür (öğrenme döngüsü); bu bileşen yalnız gösterir.
 * İçerik Stüdyosu'nun altında yaşar.
 */
import { useQuery } from "@tanstack/react-query";
import { GlSection, GlEmpty } from "@/components/ui/gl-kit";
import { GlTableWrap, GlTable, GlTHead, GlTBody, GlTr, GlTh, GlTd, GlBadge } from "@/components/ui/gl-table";
import { GraduationCap } from "lucide-react";

type Row = { briefId: number; title: string; keyword: string; writtenAt: string | null; marks: Record<string, { position: number | null; aioCited: boolean }> };

function MarkCell({ m }: { m?: { position: number | null; aioCited: boolean } }) {
  if (!m) return <span className="text-theme-xs text-gray-300">bekliyor</span>;
  if (m.position == null) return <GlBadge color="light">20+</GlBadge>;
  const color = m.position <= 3 ? "success" : m.position <= 10 ? "primary" : "warning";
  return (
    <span className="inline-flex items-center gap-1">
      <GlBadge color={color} variant="solid">{m.position}</GlBadge>
      {m.aioCited && <GlBadge color="success">AIO</GlBadge>}
    </span>
  );
}

export function ContentScorecard({ domainId }: { domainId: number }) {
  const { data } = useQuery<{ rows: Row[] }>({
    queryKey: ["content-performance", domainId],
    enabled: !!domainId,
    queryFn: async () => (await fetch(`/api/domains/${domainId}/content-performance`)).json(),
  });
  const rows = data?.rows ?? [];
  if (!rows.length) return null; // yazılmış içerik yoksa bölüm hiç görünmez

  return (
    <GlSection
      title="İçerik Karnesi"
      desc="Yazılan her içerik 30 / 60 / 90. günde hedef kelimesinde otomatik ölçülür — sonuçlar ajanın öğrenen hafızasına da işlenir."
      className="mt-6"
      bodyClass="!px-0 !py-0"
    >
      <GlTableWrap>
        <GlTable>
          <GlTHead>
            <GlTr>
              <GlTh>İçerik</GlTh><GlTh>Hedef Kelime</GlTh><GlTh>Yazılış</GlTh>
              <GlTh align="center">30. Gün</GlTh><GlTh align="center">60. Gün</GlTh><GlTh align="center">90. Gün</GlTh>
            </GlTr>
          </GlTHead>
          <GlTBody>
            {rows.map((r) => (
              <GlTr key={r.briefId}>
                <GlTd><span className="line-clamp-1 max-w-md font-medium text-gray-800 dark:text-white/90">{r.title}</span></GlTd>
                <GlTd><span className="text-theme-xs text-gray-600 dark:text-gray-300">{r.keyword}</span></GlTd>
                <GlTd><span className="text-theme-xs text-gray-500">{r.writtenAt ? new Date(r.writtenAt).toLocaleDateString("tr-TR") : "—"}</span></GlTd>
                <GlTd align="center"><MarkCell m={r.marks["30"]} /></GlTd>
                <GlTd align="center"><MarkCell m={r.marks["60"]} /></GlTd>
                <GlTd align="center"><MarkCell m={r.marks["90"]} /></GlTd>
              </GlTr>
            ))}
          </GlTBody>
        </GlTable>
      </GlTableWrap>
      <p className="flex items-center gap-1.5 px-5 py-3 text-theme-xs text-gray-400">
        <GraduationCap className="h-3.5 w-3.5" /> Ölçümler her gece 05:00'te otomatik yapılır; "bekliyor" işareti o günün henüz gelmediğini gösterir.
      </p>
    </GlSection>
  );
}
